import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import { css } from 'aphrodite';
import * as React from 'react';
import { s } from './styles';

// Props documented in ./README.md. `value`/`onValueChange` use strings to match survey answer storage.
export interface NumberedScaleInputProps extends AphroditeStyled<Omit<React.ComponentProps<'div'>, 'onChange'>> {
	min: number;
	max: number;
	value?: string;
	onValueChange: (value: string) => void;
	lowLabel?: string;
	highLabel?: string;
	disabled?: boolean;
	/** The group is a button row, not a labelable control, so pass the label's id for aria-labelledby. */
	ariaLabelledby?: string;
}

// A single-select row of number buttons for a linear/NPS scale. See ./README.md.
export function NumberedScaleInput({
	min,
	max,
	value,
	onValueChange,
	lowLabel,
	highLabel,
	disabled,
	ariaLabelledby,
	styles = [],
	...rest
}: NumberedScaleInputProps) {
	// Math.max guards corrupt bounds (max < min) — always render at least the start cell.
	const numbers: number[] = [];
	for (let n = min; n <= Math.max(min, max); n++) numbers.push(n);

	// base-ui emits [] when the cell is toggled off; forward '' so an optional scale can be un-answered.
	const handleValueChange = (groupValue: string[]) => {
		onValueChange(groupValue[0] ?? '');
	};

	return (
		<div className={css(s.container, ...styles)} {...rest}>
			<ToggleGroup
				data-slot='numbered-scale'
				role='group'
				aria-labelledby={ariaLabelledby}
				className={css(s.scale)}
				value={value !== undefined ? [value] : undefined}
				onValueChange={handleValueChange}
				multiple={false}
			>
				{numbers.map(n => (
					<Toggle
						key={n}
						value={String(n)}
						disabled={disabled}
						className={css(s.cell)}
						data-slot='numbered-scale-item'
						aria-label={String(n)}
					>
						{n}
					</Toggle>
				))}
			</ToggleGroup>
			{(lowLabel || highLabel) && (
				<div className={css(s.labels)}>
					<span>{lowLabel ?? ''}</span>
					<span>{highLabel ?? ''}</span>
				</div>
			)}
		</div>
	);
}
